import participantStatus from "enums/participantStatus";
import Promise from "bluebird";
import ko from "knockout";
import _ from "lodash";
import Message from "models/Message";
import apiCore from "services/apiCore";
import communicationService from "services/communicationService";
import LOG from "services/loggingService";
import toasts from "services/toastService";
import PubSub from "util/PubSub";
import { withRequestRetry } from "util/withRequestRetry";
import errorCallbackFactory from "util/errorCallbackFactory";

const MAKE_MESSAGE_NOTIFICATION_TOPIC =
    "messageService.notifications.makeMessage";

const activeMessages = ko.observableArray([]);

const MESSAGE_TYPES = {
    MONITOR: "monitor",
};

const MAX_RETRIES = 10;

/**
 * Message service. Handles Message retrieval, updates & subscriptions.
 * @exports services/messageService
 */
const messageService = {
    uriBuilder: communicationService.createUriBuilder(
        "/conversations/messages"
    ),

    // ===============================================================================
    // Properties
    // ===============================================================================

    /**
     * Observable array of all active Message conversation
     */
    activeMessages: activeMessages,
    /**
     * Observable array of all active user Messages. This is a subset of activeMessages.
     */
    activeUserMessages: ko.computed(function () {
        return _.filter(activeMessages(), function (message) {
            return message.isUserMessage();
        });
    }),

    /**
     * Observable array of all active agent Messages. This is a subset of activeMessages.
     */
    activeAgentMessages: ko.computed(function () {
        return _.filter(activeMessages(), function (message) {
            return message.isAgentMessage();
        });
    }),

    // ===============================================================================
    // Methods
    // ===============================================================================

    getMessage: function (messageId, options) {
        const opts = _.assign(
            {
                shouldReturnRawData: false,
            },
            options
        );

        return communicationService
            .getCommunication(this.uriBuilder, messageId)
            .then(function (messageData) {
                if (opts.shouldReturnRawData) {
                    return messageData;
                }

                return new Message(messageData);
            });
    },

    /**
     * Get the messages for a current user
     * @return {Promise}
     */
    getActiveMessages: function () {
        return apiCore.v2.get(
            messageService.uriBuilder.communicationsByType("message")
        );
    },

    /**
     * Monitor a participant on a messaging
     * @param {string} id the id of the messages to monitor.
     * @param {string} participantId the id of the participant to monitor.
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered messages should be notified
     *                  of the newly monitored messages.
     *
     * @return {promise} a promise that resolves with the {@link Message} object
     */

    monitorMessage: function (id, participantId, notifySubscribers) {
        const self = this;

        // This is a chat conversation  that the current user is currently not a participant of.  So, we have to request
        // to be added as a monitor and then wait for the new chat conversation to show up in the active chat list, and then
        // verify that the current user's monitor attribute is set to true.
        return apiCore.v2
            .post(
                messageService.uriBuilder.monitor(id, participantId),
                null,
                null
            )
            .then(function () {
                return waitForNewMessagesToBeCreated(id, MESSAGE_TYPES.MONITOR);
            })
            .then(function (message) {
                return communicationService.validateActionSucceeded(
                    message.id,
                    message.participantForCurrentUser().id,
                    Message,
                    "status",
                    participantStatus.CONNECTED,
                    self.activeMessages,
                    self.uriBuilder
                );
            })
            .then(function (message) {
                if (notifySubscribers) {
                    PubSub.Topic(MAKE_MESSAGE_NOTIFICATION_TOPIC).publish(id);
                }
                return message;
            })
            .catch(function (err) {
                LOG.error("Error while monitoring message:", err);
                if (
                    err.details &&
                    err.details.response &&
                    err.details.response.code
                ) {
                    toasts.notifyMajorAlert(
                        "digital.monitorMessage.error." +
                            err.details.response.code
                    );
                } else {
                    toasts.notifyMajorAlert(
                        "digital.monitorMessage.error.generic"
                    );
                }
            });
    },
    /**
     * Create a new Message for the current user
     * @param {Object} options the Message options object that can have the following properties:
     *              Message - the phone number/address to Message
     *              MessageUserId - the id of the user to Message
     *              MessageQueueId - the id of the queue to Message
     *              MessageFrom - the id of the queue to Message on behalf of
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered Messages should be notified
     *                  of the newly monitored Message.
     * @return {promise} a promise that resolves with the {@link Message} object
     */
    createMessage: function (options, notifySubscribers) {
        return apiCore.v2
            .post(messageService.uriBuilder.base(), null, options)
            .then(function (messageData) {
                return waitForNewMessagesToBeCreated(messageData.id).then(
                    function (message) {
                        if (notifySubscribers) {
                            PubSub.Topic(
                                MAKE_MESSAGE_NOTIFICATION_TOPIC
                            ).publish(message.id);
                        }
                        return message;
                    }
                );
            });
    },

    /**
     * Creates a new Message from the current user to the
     * provided destination address.
     * @param {string} destAddress the telephony address to Message
     * @param {Boolean} notifySubscribers indicates if subscribes to new answered Messages should be notified
     *                  of the newly monitored Message.
     * @return {promise} a promise that resolves with the {@link Message} object
     */
    makeMessage: function (destAddress, notifySubscribers) {
        return messageService.createMessage(
            { message: destAddress },
            notifySubscribers
        );
    },

    /**
     * Ends an ongoing message conversation.
     * @param {string} messageid the id of the messaging to end.
     * @param {string} participant the id of the participent to disconnect from the messaging.
     * @return {promise} a promise that resolves with the {@link Message} object
     */
    endMessage: function (id, participantId) {
        return communicationService.disconnect(
            this.uriBuilder,
            id,
            participantId,
            this.activeMessages,
            Message
        );
    },

    saveAfterMessageWork: function (messageId, participantId, data) {
        return apiCore.v2.patch(
            this.uriBuilder.participant(messageId, participantId),
            data
        );
    },
};

communicationService.subscribeToTopic(
    "conversations.messages",
    messageService.activeMessages,
    messageService.getActiveMessages,
    Message
);

/**
 * Handler for  new message that are added to a user's live
 * Message data.
 */
const onNewUserMessage = function (message) {
    LOG.debug("Message state tracked in active message ", message.id);
};

/**
 * Pushes message data into active messages, replacing any missing data,
 * but never overwriting existing data from push events. This
 * behavior ensures eventual consistency, provided that the
 * messageData is fetched after activeMessages becomes live.
 *
 * @param {object} messageData raw message data to push to active messaging
 */
const fillToActiveMessages = function (messageData) {
    const activeMessages = messageService.activeMessages;
    let message = _.find(activeMessages(), { id: messageData.id });
    if (message) {
        LOG.debug("Filling data into active messages", message);
        Message.fill(message, messageData);
    } else {
        LOG.debug("Adding Messages to activeMessages", messageData);
        message = addToActiveMessages(messageData);
    }
    return message;
};

/**
 * Adds a new Message object to the list of active Message based on
 * the provided Message data, and sets up tracking so the Message
 * will be remove when the user disconnects.
 * @param {object} messageData raw message data to add to active Messages
 */
const addToActiveMessages = function (messageData) {
    const message = new Message(messageData);
    messageService.activeMessages.push(message);
    onNewUserMessage(message);
    return message;
};

const waitForNewMessagesToBeCreated = function (messageId, waitingFor = "") {
    let resolvePromise, rejectPromise;
    const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    });

    const message = _.find(activeMessages(), { id: messageId });

    if (message) {
        if (
            message.participantForCurrentUser() &&
            message.participantForCurrentUser().monitoredParticipantId() !==
                null
        ) {
            const getActiveMessagesWithMorphedParticipantId = () =>
                messageService.getActiveMessages({
                    shouldReturnRawData: true,
                });
            pollFunction(getActiveMessagesWithMorphedParticipantId);
        } else {
            resolvePromise(message);
        }
        return promise;
    }
    function pollFunction(callback) {
        return withRequestRetry(
            callback,
            function (messageData) {
                const entity = _.find(messageData.entities, function (entity) {
                    return entity.id == messageId;
                });
                const { monitoredParticipantId } = _.find(
                    entity.participants,
                    function (participant) {
                        return (
                            participant.purpose === "user" &&
                            message.participantForCurrentUser().userId() ==
                                participant.user.id &&
                            !participant.endTime
                        );
                    }
                );

                //We know which supervisor has requested to Monitor and using waitingFor
                //we will retry until we get monitored participant id's

                if (
                    (waitingFor == MESSAGE_TYPES.MONITOR &&
                        monitoredParticipantId) ||
                    waitingFor == ""
                ) {
                    message
                        .participantForCurrentUser()
                        .monitoredParticipantId(monitoredParticipantId);
                    resolvePromise(message);
                    return;
                }
                return true;
            },
            function (error) {
                rejectPromise(error);
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.message.error",
                })(error);
            },
            { maxRetries: MAX_RETRIES, startIntervalMs: 1000 }
        )();
    }

    const pollingObj = {
        timeout: null,
        xhrRequest: null,
    };

    const subscription = messageService.activeMessages.subscribe(function () {
        const message = _.find(activeMessages(), { id: messageId });

        if (message) {
            resolvePromise(message);

            if (pollingObj.timeout) {
                clearTimeout(pollingObj.timeout);
            }

            if (pollingObj.xhrRequest) {
                pollingObj.xhrRequest.cancel();
            }

            subscription.dispose();
        }
    });

    let tries = 0;
    let messageIsDisconnected = false;

    const getMessageTimeout = function () {
        pollingObj.xhrRequest = messageService.getMessage(messageId, {
            shouldReturnRawData: true,
        });

        pollingObj.xhrRequest
            .then(function (messageData) {
                if (_.has(messageData, "participants")) {
                    if (subscription) {
                        // get rid of the subscription on success becuase we
                        // just got the latest data from the backend.
                        subscription.dispose();
                    }

                    let message = new Message(messageData);
                    // only add in the message if it is active.
                    if (!message.isDisconnected()) {
                        message = fillToActiveMessages(messageData);
                    }
                    messageIsDisconnected = message.isDisconnected();
                    resolvePromise(message);
                }
            })
            .catch(Promise.CancellationError, _.noop)
            .finally(function () {
                const message = _.find(activeMessages(), { id: messageId });

                // if the message is found or is not active then don't try anymore
                if (message || messageIsDisconnected) {
                    if (subscription) {
                        subscription.dispose();
                    }
                    pollingObj.xhrRequest = null;
                } else {
                    if (tries > 15) {
                        // stop trying to get the messages's data after 15 seconds as there
                        // was most likely an error in creating the message. This number
                        // came from Anthony Roach.
                        if (subscription) {
                            subscription.dispose();
                        }
                        rejectPromise(
                            new Error("Timeout waiting for message creation")
                        );
                    } else {
                        // The message has not been fully created yet, so try again
                        pollingObj.timeout = setTimeout(
                            getMessageTimeout,
                            1000
                        );
                        tries++;
                    }
                }
            });
    };

    // Poll for the new message after waiting for 15s
    pollingObj.timeout = setTimeout(getMessageTimeout, 1000);

    return promise;
};

ko.computed(function () {
    messageService.activeMessages(
        _.filter(messageService.activeMessages(), function (message) {
            if (_.has(message, "isActive")) {
                return message.isActive();
            }

            return false;
        })
    );
});

export default messageService;
